import { useState } from "react";
import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, Briefcase, Users } from "lucide-react";
import { createWebApplicationSchema } from "@/utils/schemaHelpers";

const Compatibility = () => {
  const [sign1, setSign1] = useState("");
  const [sign2, setSign2] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [scores, setScores] = useState({ love: 0, friendship: 0, work: 0 });

  const appSchema = createWebApplicationSchema(
    "Test de Compatibilidad Zodiacal Gratis",
    "Descubre la compatibilidad entre signos del zodiaco en amor, amistad y trabajo. Test gratuito de compatibilidad astrológica.",
    "LifestyleApplication"
  );

  const calculateCompatibility = () => {
    if (sign1 && sign2) {
      setScores({
        love: Math.floor(Math.random() * 100) + 1,
        friendship: Math.floor(Math.random() * 100) + 1,
        work: Math.floor(Math.random() * 100) + 1,
      });
      setShowResult(true);
    }
  };

  const getCompatibilityDescription = (score: number, category: 'love' | 'friendship' | 'work') => {
    const descriptions = {
      love: {
        excellent: "Su conexión es mágica y trascendental. Las estrellas se alinean perfectamente para crear una unión profunda y duradera. La pasión y el entendimiento fluyen naturalmente entre ustedes.",
        high: "Existe una fuerte atracción y comprensión mutua. Su química es innegable y comparten valores fundamentales que fortalecen el vínculo romántico.",
        good: "Tienen potencial para una relación sólida. Aunque hay diferencias, estas pueden complementarse si trabajan en la comunicación y el entendimiento mutuo.",
        medium: "La relación requiere esfuerzo consciente. Existen desafíos que superar, pero con paciencia y dedicación pueden construir algo significativo.",
        low: "Las diferencias son notables y pueden generar tensión. Se necesita mucha flexibilidad y compromiso para que funcione esta combinación.",
        veryLow: "Los astros sugieren incompatibilidad fundamental. Esta combinación enfrenta desafíos significativos que dificultan una relación armoniosa."
      },
      friendship: {
        excellent: "Forman un dúo extraordinario. Su conexión es instantánea y profunda, compartiendo intereses, valores y una energía que se potencia mutuamente.",
        high: "Son compañeros naturales. Su amistad fluye con facilidad y se apoyan mutuamente en los momentos importantes de la vida.",
        good: "Tienen bases sólidas para una buena amistad. Aunque sus personalidades difieren, esto enriquece la relación y se enseñan mutuamente.",
        medium: "La amistad puede funcionar con esfuerzo. Deben ser conscientes de sus diferencias y trabajar en encontrar terreno común.",
        low: "Existen fricciones naturales que pueden complicar la amistad. Requiere tolerancia y comprensión para mantener la armonía.",
        veryLow: "Sus energías chocan con frecuencia. Esta combinación enfrenta dificultades importantes para establecer una amistad duradera."
      },
      work: {
        excellent: "¡Equipo perfecto! Sus habilidades se complementan de manera excepcional. Juntos pueden alcanzar logros extraordinarios en cualquier proyecto.",
        high: "Colaboran eficazmente y se motivan mutuamente. Su dinámica laboral es productiva y profesional, alcanzando objetivos con facilidad.",
        good: "Pueden trabajar juntos satisfactoriamente. Aunque sus estilos difieren, esto puede aportar perspectivas valiosas al proyecto.",
        medium: "La colaboración requiere ajustes. Deben establecer roles claros y respetar sus diferencias de enfoque para lograr resultados.",
        low: "Trabajar juntos puede ser desafiante. Sus métodos y ritmos son muy diferentes, lo que puede generar conflictos profesionales.",
        veryLow: "Esta combinación enfrenta serias dificultades laborales. Los roces son frecuentes y puede ser mejor trabajar en áreas separadas."
      }
    };

    if (score >= 90) return descriptions[category].excellent;
    if (score >= 75) return descriptions[category].high;
    if (score >= 60) return descriptions[category].good;
    if (score >= 45) return descriptions[category].medium;
    if (score >= 30) return descriptions[category].low;
    return descriptions[category].veryLow;
  };

  return (
    <>
      <Helmet>
        <title>Test de Compatibilidad Zodiacal Gratis - Amor y Amistad | Dstino</title>
        <meta name="description" content="Test de compatibilidad entre signos del zodiaco gratis. Descubre tu compatibilidad en amor, amistad y trabajo con cualquier signo zodiacal." />
        <meta property="og:title" content="Test de Compatibilidad Zodiacal Gratis" />
        <meta property="og:description" content="Descubre la compatibilidad entre signos del zodiaco en amor, amistad y trabajo." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/compatibilidad`} />
      </Helmet>

      <SchemaMarkup schema={appSchema} />

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-24 pb-12 px-4">
          <div className="container mx-auto max-w-4xl">
            <Breadcrumb 
              items={[
                { name: "Inicio", url: "/" },
                { name: "Compatibilidad" }
              ]}
            />

            <h1 className="text-5xl font-bold text-center mb-4 text-glow">
            Test de Compatibilidad Zodiacal
          </h1>
          <p className="text-center text-xl text-muted-foreground mb-12">
            Descubre tu compatibilidad en amor, amistad y trabajo
          </p>

          <Card className="p-8 gradient-celestial mb-8">
            <h2 className="text-2xl font-bold mb-6 text-center">
              Selecciona los Signos
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block mb-3 font-semibold">Primer Signo</label>
                <Select value={sign1} onValueChange={setSign1}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Elige tu signo" />
                  </SelectTrigger>
                  <SelectContent>
                    {zodiacSigns.map((sign) => (
                      <SelectItem key={sign.id} value={sign.id}>
                        {sign.emoji} {sign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block mb-3 font-semibold">Segundo Signo</label>
                <Select value={sign2} onValueChange={setSign2}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Elige el otro signo" />
                  </SelectTrigger>
                  <SelectContent>
                    {zodiacSigns.map((sign) => (
                      <SelectItem key={sign.id} value={sign.id}>
                        {sign.emoji} {sign.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button 
              onClick={calculateCompatibility} 
              className="w-full"
              size="lg"
              disabled={!sign1 || !sign2}
            >
              Calcular Compatibilidad
            </Button>
          </Card>

          {showResult && sign1 && sign2 && (
            <Card className="p-8 gradient-mystical animate-in fade-in duration-500">
              <div className="text-center mb-8">
                <div className="flex justify-center items-center gap-4 mb-4">
                  <span className="text-6xl">
                    {zodiacSigns.find(s => s.id === sign1)?.emoji}
                  </span>
                  <Heart className="w-12 h-12 text-accent animate-pulse" />
                  <span className="text-6xl">
                    {zodiacSigns.find(s => s.id === sign2)?.emoji}
                  </span>
                </div>
                <h3 className="text-3xl font-bold">
                  {zodiacSigns.find(s => s.id === sign1)?.name} & {zodiacSigns.find(s => s.id === sign2)?.name}
                </h3>
              </div>

              <div className="space-y-6">
                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-8 h-8 text-accent" />
                    <h4 className="text-2xl font-bold">Amor</h4>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <span>Compatibilidad</span>
                      <span className="font-bold text-accent">{scores.love}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-accent h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${scores.love}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    {getCompatibilityDescription(scores.love, 'love')}
                  </p>
                </div>

                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-8 h-8 text-primary" />
                    <h4 className="text-2xl font-bold">Amistad</h4>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <span>Compatibilidad</span>
                      <span className="font-bold text-primary">{scores.friendship}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-primary h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${scores.friendship}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    {getCompatibilityDescription(scores.friendship, 'friendship')}
                  </p>
                </div>

                <div className="bg-card/50 backdrop-blur rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Briefcase className="w-8 h-8 text-secondary" />
                    <h4 className="text-2xl font-bold">Trabajo</h4>
                  </div>
                  <div className="mb-3">
                    <div className="flex justify-between mb-2">
                      <span>Compatibilidad</span>
                      <span className="font-bold text-secondary">{scores.work}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-secondary h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${scores.work}%` }}
                      />
                    </div>
                  </div>
                  <p className="text-muted-foreground">
                    {getCompatibilityDescription(scores.work, 'work')}
                  </p>
                </div>
              </div>
            </Card>
          )}
          </div>
        </main>
      </div>
    </>
  );
};

const zodiacSigns = [
  { id: "aries", name: "Aries", emoji: "♈" },
  { id: "tauro", name: "Tauro", emoji: "♉" },
  { id: "geminis", name: "Géminis", emoji: "♊" },
  { id: "cancer", name: "Cáncer", emoji: "♋" },
  { id: "leo", name: "Leo", emoji: "♌" },
  { id: "virgo", name: "Virgo", emoji: "♍" },
  { id: "libra", name: "Libra", emoji: "♎" },
  { id: "escorpio", name: "Escorpio", emoji: "♏" },
  { id: "sagitario", name: "Sagitario", emoji: "♐" },
  { id: "capricornio", name: "Capricornio", emoji: "♑" },
  { id: "acuario", name: "Acuario", emoji: "♒" },
  { id: "piscis", name: "Piscis", emoji: "♓" },
];

export default Compatibility;
